import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '../ui/label';
import { Textarea } from '@/components/ui/Textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Progress } from '../ui/progress';
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete';
import { useAuth } from '@/hooks/useAuth';
import { getEmployee } from '@/api/workforce';
import { Employee } from '@/types/workforce';
import {
  ChevronLeft,
  ChevronRight,
  CalendarIcon,
  Plus,
  Trash2,
  Save,
  Send,
  Download,
  Share,
  X,
  User,
  Search
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface AcaoCombinada {
  id: number;
  acao: string;
  responsavel: string;
  prazo: string;
  indicadorSucesso: string;
  status: string;
}

interface FormData {
  tipo: string;
  categorias: string[];
  projeto: string;
  tarefaWrike: string;
  dataOcorrencia: Date | null;
  situacaoContexto: string;
  observacao: string;
  impacto: {
    tipos: string[];
    detalhamento: string;
  };
  expectativaPadrao: string;
  acoesCombinadas: AcaoCombinada[];
  suporteRecursos: string;
  followUp: {
    data: Date | null;
    metrica: string;
  };
  confidencialidade: string;
  partesInformadas: string;
}

interface FeedbackCompletoProps {
  onSuccess?: () => void;
}

const FeedbackCompleto = ({ onSuccess }: FeedbackCompletoProps) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedRecipient, setSelectedRecipient] = useState<Employee | null>(null);
  const [loadingRecipient, setLoadingRecipient] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    // Cabeçalho
    tipo: '',
    categorias: [],
    projeto: '',
    tarefaWrike: '',
    dataOcorrencia: null,

    // Etapas principais
    situacaoContexto: '',
    observacao: '',
    impacto: {
      tipos: [],
      detalhamento: ''
    },
    expectativaPadrao: '',
    acoesCombinadas: [],
    suporteRecursos: '',
    followUp: {
      data: null,
      metrica: ''
    },
    confidencialidade: 'privado',
    partesInformadas: ''
  });

  const steps = [
    { id: 0, title: 'Informações Básicas', description: 'Tipo, categorias e contexto inicial' },
    { id: 1, title: 'Situação & Contexto', description: 'Descrição detalhada da situação' },
    { id: 2, title: 'Observação & Impacto', description: 'O que foi observado e seus impactos' },
    { id: 3, title: 'Expectativas & Ações', description: 'Padrões esperados e plano de ação' },
    { id: 4, title: 'Follow-up & Finalização', description: 'Acompanhamento e configurações finais' }
  ];

  const tiposFeedback = [
    { id: 'reconhecimento', label: 'Reconhecimento', color: 'green' },
    { id: 'construtivo', label: 'Construtivo', color: 'yellow' },
    { id: 'critico', label: 'Crítico', color: 'red' }
  ];

  const categorias = [
    'Técnico', 'Comportamental', 'Comunicação', 'Processos & Wrike',
    'Projetos', 'Resultados & Finanças', 'Negócios & Cliente',
    'Liderança & Equipe', 'Conflitos & Ética', 'Cultura & Valores'
  ];

  const tiposImpacto = [
    'Cliente', 'Prazo', 'Custo', 'Qualidade', 'Clima'
  ];

  const statusOptions = [
    'Pendente', 'Em Andamento', 'Concluído', 'Cancelado'
  ];

  const addAcao = () => {
    setFormData({
      ...formData,
      acoesCombinadas: [
        ...formData.acoesCombinadas,
        {
          id: Date.now(),
          acao: '',
          responsavel: '',
          prazo: '',
          indicadorSucesso: '',
          status: 'Pendente'
        }
      ]
    });
  };

  const removeAcao = (id: number) => {
    setFormData({
      ...formData,
      acoesCombinadas: formData.acoesCombinadas.filter(acao => acao.id !== id)
    });
  };

  const updateAcao = (id: number, field: keyof AcaoCombinada, value: string) => {
    setFormData({
      ...formData,
      acoesCombinadas: formData.acoesCombinadas.map(acao =>
        acao.id === id ? { ...acao, [field]: value } : acao
      )
    });
  };

  const handleCategoriaToggle = (categoria: string) => {
    const isSelected = formData.categorias.includes(categoria);
    if (isSelected) {
      setFormData({
        ...formData,
        categorias: formData.categorias.filter(c => c !== categoria)
      });
    } else {
      setFormData({
        ...formData,
        categorias: [...formData.categorias, categoria]
      });
    }
  };

  const handleImpactoToggle = (tipo: string) => {
    const isSelected = formData.impacto.tipos.includes(tipo);
    if (isSelected) {
      setFormData({
        ...formData,
        impacto: {
          ...formData.impacto,
          tipos: formData.impacto.tipos.filter(t => t !== tipo)
        }
      });
    } else {
      setFormData({
        ...formData,
        impacto: {
          ...formData.impacto,
          tipos: [...formData.impacto.tipos, tipo]
        }
      });
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSalvar = () => {
    console.log('Salvando feedback completo:', formData);
    alert('Feedback salvo como rascunho!');
  };

  const handleEnviar = () => {
    console.log('Registrando feedback completo:', formData);
    alert('Feedback registrado com sucesso!');

    // Handle success - either callback (when inside FeedbackHub) or stay on page (when standalone)
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleExportar = () => {
    console.log('Exportando feedback para PDF:', formData);
    alert('PDF gerado com sucesso!');
  };

  const handleCompartilhar = () => {
    console.log('Compartilhando feedback com gestor:', formData);
    alert('Feedback compartilhado com o gestor!');
  };

  const handleRecipientSelect = async (employeeId: string) => {
    try {
      setLoadingRecipient(true);
      const tenantDomain = user?.employee?.tenantId || 'monumenta';
      const employeeData = await getEmployee(tenantDomain, employeeId);
      setSelectedRecipient(employeeData);
    } catch (err) {
      console.error('Error fetching employee:', err);
    } finally {
      setLoadingRecipient(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            {/* Destinatário */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Search className="h-5 w-5 text-brand-primary" />
                  Destinatário <span className="text-error">*</span>
                </CardTitle>
                <CardDescription>
                  Busque o colaborador que receberá este feedback
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedRecipient ? (
                  <div className="bg-brand-primary/10 border border-brand-primary/30 rounded-monumenta p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-primary/20 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-brand-primary" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">
                            {selectedRecipient.personalInfo?.name}
                          </p>
                          <p className="text-sm text-brand-gray">
                            {selectedRecipient.currentEngagement?.roleName} • {selectedRecipient.currentEngagement?.departmentName}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedRecipient(null)}
                        className="text-brand-gray hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <EmployeeAutocomplete
                      onSelect={handleRecipientSelect}
                      navigateOnSelect={false}
                      placeholder="Buscar por nome ou email..."
                    />
                    {loadingRecipient && (
                      <p className="text-sm text-brand-gray">Carregando...</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Tipo de Feedback */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Tipo de Feedback</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={formData.tipo} 
                  onValueChange={(value) => setFormData({...formData, tipo: value})}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {tiposFeedback.map((tipo) => (
                      <div key={tipo.id} className="flex items-center space-x-2 p-4 rounded-lg border border-brand-gray/20 hover:bg-brand-dark-lighter">
                        <RadioGroupItem value={tipo.id} id={tipo.id} />
                        <Label htmlFor={tipo.id} className="text-white font-medium cursor-pointer flex-1 text-center">
                          {tipo.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Categorias */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Categorias</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {categorias.map((categoria) => (
                    <Badge
                      key={categoria}
                      variant={formData.categorias.includes(categoria) ? "default" : "outline"}
                      className={`cursor-pointer transition-colors ${
                        formData.categorias.includes(categoria)
                          ? 'bg-brand-primary text-black hover:bg-success'
                          : 'border-brand-gray/20 text-white hover:border-brand-gray/20'
                      }`}
                      onClick={() => handleCategoriaToggle(categoria)}
                    >
                      {categoria}
                      {formData.categorias.includes(categoria) && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Informações do Contexto */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Informações do Contexto</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="projeto" className="text-white">Projeto/Cliente</Label>
                    <Input
                      id="projeto"
                      value={formData.projeto}
                      onChange={(e) => setFormData({...formData, projeto: e.target.value})}
                      className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                      placeholder="Nome do projeto ou cliente"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tarefaWrike" className="text-white">Tarefa/Link Wrike</Label>
                    <Input
                      id="tarefaWrike"
                      value={formData.tarefaWrike}
                      onChange={(e) => setFormData({...formData, tarefaWrike: e.target.value})}
                      className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                      placeholder="URL da tarefa ou projeto no Wrike"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-white">Data da Ocorrência</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full bg-brand-dark-lighter border-brand-gray/20 text-white hover:bg-brand-dark justify-start"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.dataOcorrencia ? format(formData.dataOcorrencia, "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : "Selecionar data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-brand-dark-light border-brand-gray/20">
                      <Calendar
                        mode="single"
                        selected={formData.dataOcorrencia ?? undefined}
                        onSelect={(date) => setFormData({...formData, dataOcorrencia: date ?? null})}
                        initialFocus
                        className="bg-brand-dark-light text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 1:
        return (
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white">Situação/Contexto</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.situacaoContexto}
                onChange={(e) => setFormData({...formData, situacaoContexto: e.target.value})}
                className="bg-brand-dark-lighter border-brand-gray/20 text-white min-h-40"
                placeholder="Descreva detalhadamente a situação, o contexto em que ocorreu, as pessoas envolvidas, o momento e as circunstâncias. Seja específico sobre quando, onde e como a situação se desenvolveu..."
              />
              <p className="text-brand-gray text-sm mt-2">
                Dica: Use a metodologia COIN - comece pelo <strong>Contexto</strong>
              </p>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Observação */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Observação</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.observacao}
                  onChange={(e) => setFormData({...formData, observacao: e.target.value})}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white min-h-32"
                  placeholder="Descreva objetivamente o que foi observado. Foque em fatos concretos, comportamentos específicos e resultados mensuráveis. Evite interpretações ou julgamentos..."
                />
                <p className="text-brand-gray text-sm mt-2">
                  Dica: Esta é a seção <strong>Observação</strong> da metodologia COIN
                </p>
              </CardContent>
            </Card>

            {/* Impacto */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Impacto</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-white">Áreas impactadas:</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tiposImpacto.map((tipo) => (
                      <Badge
                        key={tipo}
                        variant={formData.impacto.tipos.includes(tipo) ? "default" : "outline"}
                        className={`cursor-pointer transition-colors ${
                          formData.impacto.tipos.includes(tipo)
                            ? 'bg-error text-white hover:bg-error'
                            : 'border-brand-gray/20 text-white hover:border-brand-gray/20'
                        }`}
                        onClick={() => handleImpactoToggle(tipo)}
                      >
                        {tipo}
                        {formData.impacto.tipos.includes(tipo) && (
                          <X className="ml-1 h-3 w-3" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="detalhamentoImpacto" className="text-white">Detalhamento do impacto:</Label>
                  <Textarea
                    id="detalhamentoImpacto"
                    value={formData.impacto.detalhamento}
                    onChange={(e) => setFormData({
                      ...formData,
                      impacto: { ...formData.impacto, detalhamento: e.target.value }
                    })}
                    className="bg-brand-dark-lighter border-brand-gray/20 text-white min-h-24"
                    placeholder="Explique como essa situação impacta o trabalho, a equipe, os resultados, o cliente ou a organização. Quantifique quando possível..."
                  />
                  <p className="text-brand-gray text-sm mt-2">
                    Dica: Esta é a seção <strong>Impacto</strong> da metodologia COIN
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Expectativa/Padrão */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Expectativa/Padrão</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.expectativaPadrao}
                  onChange={(e) => setFormData({...formData, expectativaPadrao: e.target.value})}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white min-h-24"
                  placeholder="Qual é o comportamento ou resultado esperado? Que padrão deveria ser seguido? Quais são as diretrizes ou políticas relevantes?"
                />
              </CardContent>
            </Card>

            {/* Ações Combinadas */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Ações Combinadas</CardTitle>
                  <Button 
                    onClick={addAcao}
                    className="bg-brand-primary text-black hover:bg-success"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Ação
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {formData.acoesCombinadas.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-brand-gray/10">
                        <TableHead className="text-white">Ação</TableHead>
                        <TableHead className="text-white">Responsável</TableHead>
                        <TableHead className="text-white">Prazo</TableHead>
                        <TableHead className="text-white">Indicador de Sucesso</TableHead>
                        <TableHead className="text-white">Status</TableHead>
                        <TableHead className="text-white w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.acoesCombinadas.map((acao) => (
                        <TableRow key={acao.id} className="border-brand-gray/10">
                          <TableCell>
                            <Input
                              value={acao.acao}
                              onChange={(e) => updateAcao(acao.id, 'acao', e.target.value)}
                              className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                              placeholder="Descreva a ação"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={acao.responsavel}
                              onChange={(e) => updateAcao(acao.id, 'responsavel', e.target.value)}
                              className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                              placeholder="Nome do responsável"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="date"
                              value={acao.prazo}
                              onChange={(e) => updateAcao(acao.id, 'prazo', e.target.value)}
                              className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={acao.indicadorSucesso}
                              onChange={(e) => updateAcao(acao.id, 'indicadorSucesso', e.target.value)}
                              className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                              placeholder="Como medir o sucesso"
                            />
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={acao.status} 
                              onValueChange={(value) => updateAcao(acao.id, 'status', value)}
                            >
                              <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map(status => (
                                  <SelectItem key={status} value={status}>{status}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeAcao(acao.id)}
                              className="text-error hover:text-error"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-brand-gray">
                    <p>Nenhuma ação adicionada ainda.</p>
                    <p className="text-sm">Clique em "Adicionar Ação" para começar.</p>
                  </div>
                )}
                <p className="text-brand-gray text-sm mt-4">
                  Dica: Esta é a seção <strong>Next Step</strong> da metodologia COIN
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {/* Suporte/Recursos necessários */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Suporte/Recursos Necessários</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.suporteRecursos}
                  onChange={(e) => setFormData({...formData, suporteRecursos: e.target.value})}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white min-h-24"
                  placeholder="Que recursos, treinamentos, ferramentas ou apoio são necessários para alcançar os objetivos definidos?"
                />
              </CardContent>
            </Card>

            {/* Follow-up */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Follow-up</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-white">Data do Follow-up</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full bg-brand-dark-lighter border-brand-gray/20 text-white hover:bg-brand-dark justify-start"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.followUp.data ? format(formData.followUp.data, "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : "Agendar follow-up"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-brand-dark-light border-brand-gray/20">
                      <Calendar
                        mode="single"
                        selected={formData.followUp.data ?? undefined}
                        onSelect={(date) => setFormData({
                          ...formData,
                          followUp: { ...formData.followUp, data: date ?? null }
                        })}
                        initialFocus
                        className="bg-brand-dark-light text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <Label htmlFor="metrica" className="text-white">Métrica de Acompanhamento</Label>
                  <Input
                    id="metrica"
                    value={formData.followUp.metrica}
                    onChange={(e) => setFormData({
                      ...formData,
                      followUp: { ...formData.followUp, metrica: e.target.value }
                    })}
                    className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                    placeholder="Como será medido o progresso? (KPIs, indicadores, etc.)"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Confidencialidade e Partes Informadas */}
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white">Confidencialidade e Partes Informadas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup 
                  value={formData.confidencialidade} 
                  onValueChange={(value) => setFormData({...formData, confidencialidade: value})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="privado" id="privado" />
                    <Label htmlFor="privado" className="text-white">Privado</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="notificar" id="notificar" />
                    <Label htmlFor="notificar" className="text-white">Notificar outras pessoas</Label>
                  </div>
                </RadioGroup>
                
                {formData.confidencialidade === 'notificar' && (
                  <div>
                    <Label htmlFor="partesInformadas" className="text-white">Pessoas a serem notificadas:</Label>
                    <Textarea
                      id="partesInformadas"
                      value={formData.partesInformadas}
                      onChange={(e) => setFormData({...formData, partesInformadas: e.target.value})}
                      className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                      placeholder="Liste os nomes e cargos das pessoas que devem receber uma cópia deste feedback..."
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-2xl font-bold">Feedback Completo</h2>
          <p className="text-brand-gray">Formato detalhado para projetos e entregas importantes</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleSalvar}
            className="text-white border-brand-gray/20 hover:bg-brand-dark-lighter"
          >
            <Save className="h-4 w-4 mr-2" />
            Salvar
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportar}
            className="text-white border-brand-gray/20 hover:bg-brand-dark-lighter"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
          <Button 
            variant="outline" 
            onClick={handleCompartilhar}
            className="text-white border-brand-gray/20 hover:bg-brand-dark-lighter"
          >
            <Share className="h-4 w-4 mr-2" />
            Compartilhar
          </Button>
        </div>
      </div>

      {/* Progress Indicator */}
      <Card className="bg-brand-dark-light border-brand-gray/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-medium">
              Etapa {currentStep + 1} de {steps.length}: {steps[currentStep].title}
            </span>
            <span className="text-brand-gray text-sm">
              {Math.round(((currentStep + 1) / steps.length) * 100)}% concluído
            </span>
          </div>
          <Progress value={((currentStep + 1) / steps.length) * 100} className="h-2" />
          <p className="text-brand-gray text-sm mt-2">{steps[currentStep].description}</p>
        </CardContent>
      </Card>

      {/* Step Content */}
      <div className="min-h-96">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center sticky bottom-0 bg-brand-dark p-4 border-t border-brand-gray/10">
        <Button 
          variant="outline" 
          onClick={prevStep}
          disabled={currentStep === 0}
          className="text-white border-brand-gray/20 hover:bg-brand-dark-lighter disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>

        <div className="flex items-center gap-2">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentStep 
                  ? 'bg-brand-primary' 
                  : index < currentStep 
                  ? 'bg-brand-blue' 
                  : 'bg-brand-dark'
              }`}
            />
          ))}
        </div>

        {currentStep === steps.length - 1 ? (
          <Button
            onClick={handleEnviar}
            className="bg-brand-primary text-black hover:bg-success"
          >
            <Send className="h-4 w-4 mr-2" />
            Registrar Feedback
          </Button>
        ) : (
          <Button 
            onClick={nextStep}
            className="bg-brand-primary text-black hover:bg-success"
          >
            Próximo
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default FeedbackCompleto;